import React, { useEffect, useState } from 'react';
import { AppRouter } from './AppRouter';
import { useSocket } from './hooks/useSocket';
import { useAppSelector, useAppDispatch } from './store';
import { RequestLoggerProvider } from './components/RequestLogger/RequestLogger';
import api from './services/api'; 
import { authService } from './services/auth.service'; 
import { requestNotificationPermission, onForegroundMessage } from './services/firebase-notification.service';
import { toast } from 'react-toastify';
import { checkAuth, stopImpersonation } from './store/auth/authSlice';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChatToast } from './components/chat/ChatToast';
import { paymentService } from './services/payment.service';
import { PaymentModal } from './components/payment/PaymentModal';
import { IDocInspector } from './components/debug/IDocInspector';

const App: React.FC = () => {
  console.log('[App] Rendering...');
  
  // Глобальний моніторинг data-i-doc
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const observer = new MutationObserver(() => {
        const elements = document.querySelectorAll('[data-i-doc]');
        console.group('[Global IDoc Monitor]');
        console.log(`Found ${elements.length} tracked elements:`);
        elements.forEach(el => console.log(el.getAttribute('data-i-doc'), el));
        console.groupEnd();
      });
      observer.observe(document.body, { attributes: true, subtree: true, childList: true });
      return () => observer.disconnect();
    }
  }, []);
  
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Отримуємо стани
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const user = useAppSelector((state) => state.auth.user);
  const activeConversationId = useAppSelector((state) => state.chat.activeConversationId);
  
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  console.log('[App] Auth state:', { isAuthenticated });

  // Ініціалізуємо глобальний менеджер сокет-з'єднання
  useSocket(isAuthenticated, activeConversationId);

  // Ініціалізація пуш-повідомлень
  useEffect(() => {
    console.log('[App] Push notification effect triggered. isAuthenticated:', isAuthenticated);
    
    let unsubscribe: (() => void) | undefined;

    const initNotifications = async () => {
      if (isAuthenticated) {
        // Спочатку чекаємо ініціалізації Firebase та отримання дозволу
        await requestNotificationPermission();

        // Тільки після цього підписуємось на повідомлення
        unsubscribe = onForegroundMessage((payload) => {
          console.log('[App] Foreground message received:', payload);
          
          // Отримуємо дані (New Contract)
          const title = payload.notification?.title || payload.data?.title;
          const body = payload.notification?.body || payload.data?.body;
          
          // Технічні дані
          const type = payload.data?.type;
          const chatId = payload.data?.chatId || payload.data?.conversationId;
          const senderAvatar = payload.data?.senderAvatar || payload.data?.avatar; 

          if (title || body) {
            // 1. 💬 Чат (CHAT_MESSAGE)
            if (type === 'CHAT_MESSAGE') {
              toast(
                <ChatToast 
                  title={title || 'Нове повідомлення'} 
                  body={body || ''} 
                  avatar={senderAvatar} 
                />,
                {
                  autoClose: 5000,
                  hideProgressBar: true,
                  closeButton: false,
                  className: 'chat-toast-notification',
                  onClick: () => {
                    console.log('Chat Toast clicked. ChatID:', chatId);
                    if (chatId) {
                      navigate(`/chat/${chatId}`);
                    }
                  }
                }
              );
            } 
            // 2. 📰 Новини (NEWS)
            else if (type === 'NEWS') {
              toast.info(
                <div>
                  <strong>{title}</strong>
                  <div>{body}</div>
                </div>,
                {
                  autoClose: 5000,
                  onClick: () => {
                    navigate('/news'); 
                  }
                }
              );
            }
            // 3. 📅 Календар (CALENDAR_EVENT)
            else if (type === 'CALENDAR_EVENT') {
              toast.info(
                <div>
                  <strong>{title}</strong>
                  <div>{body}</div>
                </div>,
                {
                  autoClose: 5000,
                  onClick: () => {
                    navigate('/calendar');
                  }
                }
              );
            }
            // 4. 💰 Оплата (PAYMENT_SUCCESS)
            else if (type === 'PAYMENT_SUCCESS') {
              // Очищаємо прапорець оплати
              sessionStorage.removeItem('payment_in_progress');
              
              toast.success(
                <div>
                  <strong>{title}</strong>
                  <div>{body}</div>
                </div>,
                {
                  autoClose: 5000,
                  onClick: () => {
                    navigate('/payment/success');
                  }
                }
              );
              
              if (window.location.pathname !== '/payment/success') {
                 navigate('/payment/success');
              } 
            }
            // 5. ⚠️ Помилка оплати (PAYMENT_FAILED)
            else if (type === 'PAYMENT_FAILED') {
              // Очищаємо прапорець оплати
              sessionStorage.removeItem('payment_in_progress');
              
              toast.error(
                <div>
                  <strong>{title}</strong>
                  <div>{body}</div>
                </div>,
                {
                  autoClose: 7000,
                  onClick: () => {
                    navigate('/settings', { state: { activeTab: 'subscription' } });
                  }
                }
              );
            }
            // 6. ⭐ Запит на відгук (APPOINTMENT_REVIEW)
            else if (type === 'APPOINTMENT_REVIEW') {
              toast.info(
                <div>
                  <strong>{title}</strong>
                  <div>{body}</div>
                </div>,
                {
                  autoClose: 7000,
                  onClick: () => {
                    navigate('/appointments');
                  }
                }
              );
            }
            // 7. 🔔 Звичайні сповіщення (NOTIFICATION) або інші типи
            else {
              toast.info(
                <div>
                  <strong>{title}</strong>
                  <div>{body}</div>
                  {process.env.NODE_ENV === 'development' && type && (
                    <div style={{ fontSize: '0.8em', marginTop: '5px', opacity: 0.8, borderTop: '1px solid #fff', paddingTop: '5px' }}>
                      DEBUG: Type={type}
                    </div>
                  )}
                </div>,
                {
                  autoClose: 5000,
                  onClick: () => {
                    navigate('/notifications');
                  }
                }
              );
            }
          }
        });
      }
    };

    initNotifications();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [isAuthenticated, navigate]);

  // Слухаємо примусове завершення impersonation з api.ts (401 revoked)
  useEffect(() => {
    const handler = () => dispatch(stopImpersonation());
    window.addEventListener('impersonation:revoked', handler);
    return () => window.removeEventListener('impersonation:revoked', handler);
  }, [dispatch]);

  // Перевірка авторизації при старті
  useEffect(() => {
    const initAuth = async () => {
      const accessToken = authService.getAccessToken();
      const refreshToken = authService.getRefreshToken();
      const isAuthPage = window.location.pathname.startsWith('/auth');

      console.log('[App] initAuth check. Tokens exist:', { 
        hasAccess: !!accessToken, 
        hasRefresh: !!refreshToken,
        isAuthPage 
      });

      // Якщо є токени, спробуємо відновити сесію через Redux
      if (accessToken && refreshToken) {
        console.log('[App] Dispatching checkAuth...');
        await dispatch(checkAuth());
        console.log('[App] checkAuth dispatched');
      }
    };

    initAuth();
  }, [dispatch]);

  // Перевірка підписки (Paywall)
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    // Скидаємо модалку, якщо користувач не авторизований
    if (!isAuthenticated || !user) {
      setShowPaymentModal(false);
      return;
    }

    // Не показувати модалку на сторінці успішної оплати
    if (location.pathname === '/payment/success') {
      setShowPaymentModal(false);
      return;
    }

    const checkSubscription = async () => {
      if (isAuthenticated && user && user.roles.includes('client')) {
        // Затримка перед перевіркою (наприклад, 3 секунди після завантаження)
        // Можна налаштувати n секунд
        const DELAY_SECONDS = 3; 
        
        timeoutId = setTimeout(async () => {
          try {
            const status = await paymentService.getMySubscription();
            if (!status.isActive) {
              setShowPaymentModal(true);
            } else {
              setShowPaymentModal(false);
            }
          } catch (error) {
            console.error('Failed to check subscription status', error);
            // У разі помилки можна вирішити, чи блокувати доступ, чи ні.
            // Поки що не блокуємо, якщо API недоступне.
          }
        }, DELAY_SECONDS * 1000);
      }
    };

    checkSubscription();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isAuthenticated, user, location.pathname]);

  // Polling при поверненні фокусу (Visibility Change)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const paymentInProgress = sessionStorage.getItem('payment_in_progress');
        
        if (paymentInProgress === 'true' && isAuthenticated && user) {
          console.log('[App] Payment in progress detected. Checking status...');
          try {
            const status = await paymentService.getMySubscription();
            
            if (status.isActive) {
              console.log('[App] Payment success confirmed via polling!');
              sessionStorage.removeItem('payment_in_progress');
              toast.success('Оплата успішна! Підписка активована.');
              navigate('/payment/success');
              // Також закриваємо модалку, якщо вона була відкрита
              setShowPaymentModal(false);
            } else if (['failed', 'declined', 'cancelled', 'payment_failed'].includes(status.status)) {
               console.log('[App] Payment failed confirmed via polling.');
               sessionStorage.removeItem('payment_in_progress');
               toast.error('Оплата не пройшла.');
               // Можна перенаправити на налаштування або залишити як є
            } else {
               console.log('[App] Payment status still pending/none:', status.status);
               // Якщо все ще pending, нічого не робимо, чекаємо пуша або наступного фокусу
            }
          } catch (error) {
            console.error('[App] Failed to check subscription on visibility change', error);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, user, navigate]);

  return (
    <RequestLoggerProvider axiosInstance={api}>
      <AppRouter />
      {/* {process.env.NODE_ENV === 'development' && <RequestLoggerDisplay />} */}
      <PaymentModal open={showPaymentModal} onClose={() => {}} canClose={false} />
      <IDocInspector />
    </RequestLoggerProvider>
  );
};

export default App;
